import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
const easeOut = [0.16, 1, 0.3, 1] as const;
export function HeroSection() {
  const {
    lang
  } = useLanguage();
  const trust = [{
    value: '100+',
    label: lang === 'es' ? 'Negocios impulsados' : 'Businesses launched'
  }, {
    value: '100%',
    label: lang === 'es' ? 'Estrategia personalizada' : 'Personalized strategy'
  }, {
    value: '< 2 sem',
    valueEn: '< 2 wks',
    label: lang === 'es' ? 'Entrega promedio' : 'Average delivery'
  }];
  const fadeUp = (delay: number) => ({
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.6,
      ease: easeOut,
      delay
    }
  });
  const fade = (delay: number, duration = 0.6) => ({
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    transition: {
      duration,
      ease: easeOut,
      delay
    }
  });
  return <section id="inicio" className="relative bg-[#FEFCFA] w-full lg:min-h-[90vh] overflow-hidden" data-id="element-212">
      {/* Acento lila muy sutil en el fondo */}
      <div className="pointer-events-none absolute -top-40 right-[10%] w-[520px] h-[520px] rounded-full opacity-50 blur-[140px]" style={{
      background: '#EDE9FE'
    }} data-id="element-213" />

      <div className="relative max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-[54%_46%] lg:grid-rows-[1fr_auto] lg:min-h-[90vh] lg:items-center" data-id="element-214">
        {/* ─────────────── TEXT BLOCK ─────────────── */}
        <div className="order-1 lg:col-start-1 lg:row-start-1 lg:self-center flex flex-col justify-center px-6 pt-10 lg:pt-0 lg:pl-[6vw] lg:pr-10" data-id="element-215">
          {/* Eyebrow */}
          <motion.div {...fade(0.1)} className="flex items-center mb-6" data-id="element-216">
            <span className="inline-block w-[28px] h-[2px] bg-[#A78BFA] mr-3" data-id="element-217" />
            <span className="font-sans text-[10.5px] lg:text-[11px] font-semibold tracking-[3px] uppercase text-[#6B3FA0]" data-id="element-218">
              {lang === 'es' ? 'Miami · Consultoría Digital' : 'Miami · Digital Consulting'}
            </span>
          </motion.div>

          {/* Headline — focal point */}
          <h1 className="font-display font-bold text-[#0F0A1E] text-[34px] leading-[1.18] sm:text-[40px] lg:text-[58px] lg:leading-[1.08] tracking-[-0.5px] lg:tracking-[-1.5px] mb-5 lg:mb-6" data-id="element-219">
            {lang === 'es' ? <>
                <motion.span {...fadeUp(0.2)} className="block" data-id="element-220">
                  Tu negocio digital,
                </motion.span>
                <motion.span {...fadeUp(0.32)} className="block" data-id="element-221">
                  construido desde tu
                </motion.span>
                <motion.span {...fadeUp(0.44)} className="block italic font-semibold text-[#6B3FA0]" data-id="element-222">
                  identidad y propósito.
                </motion.span>
              </> : <>
                <motion.span {...fadeUp(0.2)} className="block" data-id="element-223">
                  Your digital business,
                </motion.span>
                <motion.span {...fadeUp(0.32)} className="block" data-id="element-224">
                  built from your
                </motion.span>
                <motion.span {...fadeUp(0.44)} className="block italic font-semibold text-[#6B3FA0]" data-id="element-225">
                  identity and purpose.
                </motion.span>
              </>}
          </h1>

          {/* Supporting text — short and refined */}
          <motion.p {...fade(0.56)} className="font-sans text-[15px] lg:text-[17px] leading-[1.65] text-[#5a5368] max-w-[480px] mb-9 lg:mb-10" data-id="element-226">
            {lang === 'es' ? 'Diseñamos una presencia digital clara, estratégica y alineada con el valor real de tu negocio.' : 'We design a clear, strategic digital presence aligned with the real value of your business.'}
          </motion.p>

          {/* CTA hierarchy */}
          <motion.div {...fade(0.68)} className="flex flex-col items-start gap-4" data-id="element-227">
            {/* Primary CTA + microcopy */}
            <div className="flex flex-col items-stretch sm:items-start w-full sm:w-auto" data-id="element-228">
              <a href="https://calendly.com/inkovacreation" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-[#6B3FA0] text-white font-sans font-semibold text-[13px] tracking-[0.6px] uppercase px-10 py-4 rounded-[50px] shadow-[0_10px_28px_rgba(107,63,160,0.28)] transition-all duration-300 hover:bg-[#3D2278] hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(107,63,160,0.36)]" data-id="element-229">
                {lang === 'es' ? 'Da el primer paso' : 'Take the first step'}
              </a>
              <span className="font-sans text-[11px] tracking-[0.4px] text-[#9b8aaa] mt-3 text-center sm:text-left" data-id="element-230">
                {lang === 'es' ? 'Sesión diagnóstica · 30 min · USD 50' : 'Diagnostic session · 30 min · USD 50'}
              </span>
            </div>

            {/* Secondary CTA — lighter */}
            <a href="#servicios" className="group inline-flex items-center gap-2 font-sans font-medium text-[13px] tracking-[0.4px] text-[#6B3FA0] hover:text-[#3D2278] transition-colors duration-300" data-id="element-231">
              <span className="border-b border-[#6B3FA0]/30 group-hover:border-[#3D2278]/60 pb-0.5 transition-colors" data-id="element-232">
                {lang === 'es' ? 'Ver servicios' : 'View services'}
              </span>
              <span className="transition-transform duration-300 group-hover:translate-x-1" data-id="element-233">
                →
              </span>
            </a>
          </motion.div>
        </div>

        {/* ─────────────── PORTRAIT BLOCK ─────────────── */}
        <motion.div {...fade(0, 1.2)} className="order-2 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center relative w-full h-[52vw] sm:h-[44vw] lg:h-[74vh] flex items-end lg:items-start justify-center lg:justify-end overflow-hidden mt-2 lg:mt-0 lg:pr-[2vw] lg:-translate-y-[2vh]" data-id="element-234">
          {/* Glow lila detrás del retrato */}
          <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 lg:left-[58%] lg:-translate-y-[42%] w-[68%] h-[78%] lg:w-[78%] lg:h-[72%] rounded-full z-0 blur-[55px] lg:blur-[70px]" style={{
          background: 'radial-gradient(ellipse at center, #EDE9FE 0%, rgba(237,233,254,0.45) 48%, transparent 72%)'
        }} data-id="element-235" />
          <img src="https://cdn.magicpatterns.com/uploads/8wA1hfb42Whxpi23trRcAX/anny_png.png" alt="Annysol Navarro, fundadora de Inkova Creation" loading="eager" className="relative z-[1] h-full w-auto max-w-full object-contain [object-position:center] lg:w-full lg:h-auto lg:min-h-full lg:object-cover lg:[object-position:top_center]" style={{
          filter: 'drop-shadow(0 22px 44px rgba(167, 139, 250, 0.22))'
        }} data-id="element-236" />
        </motion.div>

        {/* ─────────────── STATS BLOCK ─────────────── */}
        <motion.div {...fade(0.8)} className="order-3 lg:col-start-1 lg:row-start-2 lg:self-center grid grid-cols-3 gap-2.5 sm:gap-4 px-6 lg:px-0 lg:pl-[6vw] lg:pr-10 mt-8 lg:mt-2 lg:mb-[7vh] pb-20 lg:pb-0 max-w-[520px] lg:max-w-[560px]" data-id="element-237">
          {trust.map((item, index) => <Fragment key={index} data-id="element-238">
              <div className="flex flex-col items-start bg-white rounded-[16px] border border-[#E5DDF5] shadow-[0_6px_18px_rgba(107,63,160,0.06)] px-3.5 py-4 lg:px-6 lg:py-7" data-id="element-239">
                <span className="font-display font-bold text-[22px] lg:text-[34px] text-[#0F0A1E] leading-none" data-id="element-240">
                  {lang === 'en' && item.valueEn ? item.valueEn : item.value}
                </span>
                <span className="font-sans text-[9.5px] lg:text-[11px] tracking-[0.6px] uppercase text-[#9b8aaa] mt-2.5 leading-snug" data-id="element-241">
                  {item.label}
                </span>
              </div>
            </Fragment>)}
        </motion.div>
      </div>
    </section>;
}